<template>
    <div id="app" class="min-w-[900px] min-h-[100vh] h-screen flex">
        <div id="login" class="w-full h-full flex">
            <div class="flex-1 bg-[url('/src/assets/img/login-3.jpg')] bg-cover">
                <div class="w-full h-full flex justify-center items-center bg-black/[0.6]">
                    <img src="@/assets/img/hrnflex_wh_logo.png" alt="white_logo" class="h-8" />
                </div>
            </div>
            <form v-if="!accountLockout" class="flex-1 flex flex-col justify-center items-center bg-white" @submit="onSubmit" ref="loginForm">
                <div class="px-[10%] flex flex-col gap-8">
                    <div class="text-center w-full">
                        <h1 class="font-heading-lg-bold">Log In</h1>
                    </div>
                    <div class="flex flex-col gap-4">
                        <ElementsInput
                            v-model="loginRequest.loginId"
                            name="Email Address"
                            placeholder="Enter Email Address"
                            :error="loginRequestError.loginId"
                            inputName="loginId"
                            inputtype="email"
                            :autoComplete="'email'" />
                        <ElementsInput
                            v-model="loginRequest.loginPwd"
                            name="Password"
                            placeholder="Enter Password"
                            :error="loginRequestError.loginPwd"
                            inputName="loginPwd"
                            inputtype="password"
                            ref="password"
                            :autoComplete="'current-password'" />
                    </div>
                    <div class="flex flex-col gap-2 items-center">
                        <ElementsButton text="Login" inputtype="submit" />
                        <a class="font-body-sm-semibold text-primary-3 leading-[2.5rem] underline hover:text-primary-5" href="/find_password">Forgot password?</a>
                    </div>
                </div>
            </form>

            <div v-if="accountLockout" class="flex-1 flex flex-col justify-center items-center">
                <div class="px-[10%] flex flex-col gap-8">
                    <div class="flex flex-col items-center">
                       <h1 class="font-heading-lg-bold">Login Failed</h1>
                       <h3 class="font-body-sm-medium text-content-basic-2">
                           Your account has been temporarily locked for security reasons.
                       </h3>
                   </div>
   
                   <div class="w-ful">
                       <div class="w-full h-20 flex justify-center items-center gap-3 p-5 border rounded-02">
                           <p class="font-heading-md-medium">Unlock in</p>
                           <p class="font-heading-md-medium text-primary-5">{{ lockRemainTime }}</p>
   
                       </div>
                   </div>
                </div>
            </div>
        </div>
    </div>
    <Teleport to="body">
        <ElementsModal v-model="noticePopup" :name="loginRequestError.message" button-text="OK" :warn="true" @button-event="closeNoticePopup" />
        <ElementsNotification v-model="axiosNoti" :notificationMsg="loginRequestError.message" :warning="axiosNoti" />

        <div v-if="showChangePasswordPopup" class="fixed top-0 w-full h-full bg-black/50 z-[80] flex justify-center items-center">
            <div class="border rounded-lg shadow shadow-gray-100 bg-white w-[40rem]">
                <div class="px-7 py-4 w-full flex justify-between items-center">
                    <span class="text-xl font-bold"> Change Password Request </span>
                    <a href="javascript:void(0)" class="pl-40" @click="closeChangePasswordPopup">
                        <img src="@/assets/img/app-popup-1.svg" alt="app-popup-1" />
                    </a>
                </div>

                <div class="w-full h-px bg-gray-200"></div>
                <div class="w-full py-20 max-h-[80vh] overflow-y-auto">
                    <div v-if="changePasswordStep==='INIT'" class="flex flex-col justify-center items-center gap-2">
                        <img src="@/assets/img/shield-icon.svg" alt="shield" />
                        <p class="font-inter-heading-19 text-content-basic-1">
                            For your account security, please update your password.<br />
                            Follow the requirements below to set a strong and safe password.
                        </p>
                    </div>

                    <div v-if="changePasswordStep==='EDIT'" class="w-full px-8 flex flex-col gap-3 justify-center items-start">
                        <ElementsInput
                            v-model="newPassword"
                            class="w-full"
                            name="New Password"
                            placeholder="Enter Password"
                            :error="newPasswordError"
                            inputtype="password"
                            ref="password"
                            :autoComplete="'current-password'"
                            :full="true" />
                        <ElementsInput
                            v-model="confirmPassword"
                            class="w-full"
                            name="Confirm Password"
                            placeholder="Enter Password"
                            :error="confirmPasswordError"
                            inputtype="password"
                            ref="password"
                            :autoComplete="'current-password'"
                            :full="true" />
                    </div>

                </div>

                <div>
                    <div class="w-full h-px bg-gray-200"></div>
                    <div v-if="changePasswordStep==='INIT'" class="px-4 py-4 flex justify-center gap-2">
                        <DesignButton
                            v-if="passwordSnoozeCount < 1"
                            class="w-56"
                            :btnText="`Remind Me in ${expireDays} Days`"
                            btnSize="LG"
                            btnType="QUATERNARY"
                            :btnDisabled="false"
                            @click-event="snoozePassword"
                        />
                        <DesignButton
                            class="w-56"
                            btnText="Change Now"
                            btnSize="LG"
                            btnType="PRIMARY"
                            :btnDisabled="false"
                            @click-event="changePasswordStep = 'EDIT'"
                        />
                    </div>
                    <div v-if="changePasswordStep==='EDIT'" class="px-4 py-4 flex justify-center gap-2">
                        <DesignButton
                            class="w-56"
                            btnText="Save"
                            btnSize="LG"
                            btnType="PRIMARY"
                            :btnDisabled="false"
                            @click-event="changePassword"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div v-if="showChangePasswordConfirmPopup" class="fixed top-0 w-full h-full bg-black/50 z-[80] flex justify-center items-center">
            <div class="border rounded-lg shadow shadow-gray-100 bg-white">
                <div class="w-[25rem] h-32 flex flex-col justify-center items-center gap-2">
                    <img src="@/assets/img/change-password-confirm.svg" alt="Change Password Confirmation" />
                    <p class="font-inter-body-2 text-content-basic-1">
                        Your password has been<br /> 
                        successfully changed.
                    </p>
                </div>

                <div class="w-[25rem] h-24 flex flex-col justify-center items-center">
                    <DesignButton
                        class="w-56"
                        btnText="Go to Login"
                        btnSize="LG"
                        btnType="PRIMARY"
                        :btnDisabled="false"
                        @click-event="showChangePasswordConfirmPopup = false"
                    />
                </div>
            </div>
        </div>

        <ConfirmPopup v-model="showReusePopup" :confirmDescription="`Password cannot reuse one of the last ${passwordReuseBlockCount} passwords.`" buttonText="OK" />
    </Teleport>
</template>

<script>
import { PasswordPolicyUtil } from '@/plugins/app-util';
import ElementsModal from '@/components/elements/ElementsModal.vue';
import DesignButton from '@/components/design-system/design-button.vue';
import ConfirmPopup from '@/components/ConfirmPopup.vue';

export default {
    components: {
        ElementsModal,
        DesignButton,
        ConfirmPopup,
    },
    data() {
        return {
            result: false,
            items: ['Login'],
            loginRequestError: {
                loginId: '',
                loginPwd: '',
                message: '',
            },
            loginRequest: {
                selectPosition: 'admin',
                loginId: '',
                loginPwd: '',
            },
            // 로그인 시 실제로 전송된 비밀번호. 만료 강제 변경에서 currentPassword 로 함께 보낸다.
            // v-model 대신 폼 전송값을 쓰는 이유는 브라우저 자동완성 시 v-model 이 비어 있을 수 있기 때문이다.
            submittedLoginPwd: '',
            noticePopup: false,
            axiosNoti: false,

            accountLockout: false,

            passwordSnoozeCount: 0,
            employeeSeq: null,

            expireDays: 60,
            showChangePasswordPopup: false,
            changePasswordStep: 'INIT',
            newPassword: '',
            newPasswordError: '',
            confirmPassword: '',
            confirmPasswordError: '',

            remainTime: 0,
            remainTimer: null,
            
            passwordReuseBlockCount: 0,

            showChangePasswordConfirmPopup: false,
            showReusePopup: false,
        };
    },
    computed: {
        lockRemainTime() {
            const s = Math.max(0, Math.floor(this.remainTime));
            const m = String(Math.floor(s / 60)).padStart(2, '0');
            const sec = String(s % 60).padStart(2, '0');
            return `${m}:${sec}`;
        },
    },
    mounted() {
        this.initLoginRequest();
    },
    methods: {
        initLoginRequest() {
            this.loginRequest.loginId = '';
            this.loginRequest.loginPwd = '';
        },
        errMsgReset() {
            this.loginRequestError.loginId = '';
            this.loginRequestError.loginPwd = '';
        },
        isValid() {
            //비어있는 갯수 체크
            let notVaild = 0;

            //이메일 형식 체크
            let lowString = this.loginRequest.loginId.toLowerCase().trim();
            const formatEmail = /^([0-9a-z!#$%&'*\\+\-/=?^_`{|}~]+(?:\.[0-9a-z!#$%&'*\\+\-/=?^_`{|}~]+)*)@([0-9a-z]+(?:-[0-9a-z]+)*(?:\.[0-9a-z]+)*)$/.test(lowString);

            //--------------
            if (!formatEmail) {
                this.loginRequestError.loginId = 'Please enter a valid email address';
                notVaild += 1;
            }
            if (this.loginRequest.loginId == '') {
                this.loginRequestError.loginId = 'Please enter a valid email address';
                notVaild += 1;
            }
            if (this.loginRequest.loginPwd == '') {
                this.loginRequestError.loginPwd = 'Please provide your login password';
                notVaild += 1;
            }
            //--------------

            return notVaild == 0;
        },
        login() {
            //처음에 errMsgReset 해줌
            this.errMsgReset();

            //비어있는지 체크
            if (!this.isValid()) {
                return;
            }

            this.connect();
        },
        connect() {
            const formProps = Object.fromEntries(new FormData(this.$refs['loginForm']));
            this.submittedLoginPwd = formProps.loginPwd ?? '';

            let url = this.$api('uri', 'post-sign-in');

            this.$axios
                .post(url, formProps)
                .then((res) => {
                    console.log(url + ':result', res);
                    location.href = '/login/mfa';
                })
                .catch((err) => {
                    //억지로 포커스 해제
                    document.activeElement.blur();

                    if (err.response == undefined) {
                        this.axiosNoti = true;
                        this.loginRequestError.message = 'Network Error';
                        return console.error(err);
                    }

                    if (err.response.data.code === 'HR_SIGN_IN_EXPIRED_PASSWORD_403_FAILED') {
                        this.passwordSnoozeCount = err.response.data.data.passwordSnoozeCount;
                        this.employeeSeq = err.response.data.data.employeeSeq;
                        this.showChangePasswordPopup = true;
                        return;
                    }

                    if (err.response.data.code === 'HR_SIGN_IN_403_FAILED' && err.response.data.data?.lockedUntil != null) {
                        this.accountLockout = true;
                        this.lockedUntil = err.response.data.data.lockedUntil;
                        // LOCKED_UNTIL을 서버에서 받아서 남은 초 계산
                        const unlockAt = new Date(this.lockedUntil);
                        this.remainTime = Math.floor((unlockAt - Date.now()) / 1000);
                        this.remainTimer = setInterval(() => {
                            this.remainTime -= 1;
                            if (this.remainTime <= 0) {
                                this.remainTime = 0;
                                this.accountLockout = false;
                                clearInterval(this.remainTimer);
                                this.remainTimer = null;
                            }
                        }, 1000);
                        return;
                    }

                    const status = err.response.status;
                    const errorCode = err.response.data.code;

                    switch (true) {
                        case status == 400:
                            this.loginRequestError.message = "can't empty";
                            break;
                        case status == 403: {
                            this.noticePopup = true;
                            this.loginRequestError.message = 'You have entered an invalid username or password';
                            break;
                        }
                        case errorCode == "HR_SIGN_IN_401_SUSPENDED": { // Gone - 리소스 삭제 ( Company )
                            this.noticePopup = true;
                            this.loginRequestError.message = "This account has been suspended. Please contact your administrator";
                            break;
                        }
                        case errorCode == "HR_SIGN_IN_401_RESIGNED": { //Unauthorized -권한 없음 (Employee)
                            this.noticePopup = true;
                            this.loginRequestError.message = "This account is no longer active. Please contact your HR for assistance";
                            break;
                        }
                    }

                })
                .finally(() => {});
        },
        closeNoticePopup() {
            this.noticePopup = false;
        },
        onSubmit(event) {
            event.preventDefault();
            this.login();
        },
        closeChangePasswordPopup() {
            this.changePasswordStep = 'INIT';
            this.newPassword = '';
            this.newPasswordError = '';
            this.confirmPassword = '';
            this.confirmPasswordError = '';
            this.showChangePasswordPopup = false;
        },
        snoozePassword() {
            const url = this.$api('uri', 'post-snooze-password');
            this.$axios
                .post(url, {
                    employeeSeq: this.employeeSeq,
                    expireDays: 60,
                })
                .then((res) => {
                    this.showChangePasswordPopup = false;
                    this.connect();
                })
                .catch((err) => {});
        },
        changePassword() {
            this.newPasswordError = '';
            this.confirmPasswordError = '';

            if (this.newPassword !== this.confirmPassword) {
                this.confirmPasswordError = 'Passwords do not match. Please try again.';
                return false;
            }

            const result = PasswordPolicyUtil.validatePassword(this.newPassword);
            if (!result.valid) {
                this.newPasswordError = result.message;
                return false;
            }

            const url = this.$api('uri', 'put-password');
            this.$axios
                .put(url, {
                    // 방금 로그인에 성공한 비밀번호를 현재 비밀번호로 함께 전송한다
                    currentPassword: this.submittedLoginPwd,
                    newPassword: this.newPassword,
                })
                .then((res) => {
                    this.closeChangePasswordPopup();
                    this.showChangePasswordConfirmPopup = true;
                    this.showChangePasswordPopup = false;
                    this.submittedLoginPwd = '';
                    this.initLoginRequest();
                })
                .catch((err) => {
                    const errorCode = err.response.data.code;

                    if (errorCode === 'HR_EMPLOYEE_PASSWORD_UPDATE_403_REUSED_FAILED') {
                        this.passwordReuseBlockCount = err.response.data.data.passwordReuseBlockCount;
                        this.showReusePopup = true;
                        return;
                    }

                    if (errorCode === 'HR_EMPLOYEE_PASSWORD_UPDATE_403_MISMATCH_FAILED') {
                        this.newPasswordError = 'Could not verify your current password. Please log in again.';
                        return;
                    }

                    if (errorCode === 'HR_EMPLOYEE_PASSWORD_UPDATE_400_POLICY_FAILED') {
                        this.newPasswordError = err.response.data.data
                            || 'Password does not meet the password policy.';
                        return;
                    }

                    console.error(err.response.status);
                });
        }
    },
};
</script>
